<script setup lang="ts">
import { defineEmits, computed, defineProps, onMounted, ref, watch } from "vue";
import Payment from "@/models/Payment.js";
import InputPrice from "@/components/ui/InputPrice.vue";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const props = defineProps({
  show: {
    type: Boolean,
  },
  payableType: {
    type: String,
  },
  payableId: {
    type: Number,
  },
  modes: {
    type: Array,
  },
  statusList: {
    type: Array,
  },
  dueAmount: {
    type: Number,
  },
  currency: {
    type: String,
  },
})
const emit = defineEmits(['payment:saved', 'close'])
const showModal = ref(false)
const mode = ref(undefined)
const status = ref(props.statusList?.[0] ?? [])
const paidAmount = ref(Number(props.dueAmount) ?? 0)
const reference = ref('')
const disableButton = computed(() => mode.value && status.value === undefined)

onMounted(() => {
  paidAmount.value = Number(props.dueAmount) ?? 0
})

watch(
  () => props.dueAmount,
  () => {
    paidAmount.value = Number(props.dueAmount) ?? 0
  }
)

watch(
  () => props.show,
  () => {
    showModal.value = props.show ?? false
  }
)

const validationErrors = ref({});
const onSubmitPayment = async () => {
  validationErrors.value = {};
  const paymentModel = new Payment({
    payable_type: props.payableType,
    payable_id: props.payableId,
    mode: mode.value,
    status: status.value,
    currency: props.currency,
    paid_amount: paidAmount.value,
    reference: reference.value
  });

  try {
    await paymentModel.save();
    showModal.value = false
    emit('close')
    emit('payment:saved', paymentModel)
  } catch (error) {
    console.log("Error", error);
    validationErrors.value = error.response.data.message;
  }

};
const onUpdateDialog = (isOpen) => {
  if (! isOpen) {
    showModal.value = false
    emit('close')
  }
}

</script>

<template>
  <Dialog :open="showModal" @update:open="onUpdateDialog">
    <DialogScrollContent size="md">
      <DialogHeader>
        <DialogTitle>
          {{ $t('Create Payment') }}
        </DialogTitle>
      </DialogHeader>
      <div v-if="validationErrors.payable" class="text-danger">
        {{ validationErrors.payable[0] }}
      </div>
      <div class="">
        <div class="form-group mb-3">
          <label for="frm-create-payment-mode">{{ $t('Payment method:') }}</label>
          <select id="frm-create-payment-mode" name="mode" :class="['form-control', validationErrors.mode ? 'is-invalid' : '']" v-model="mode">
            <option v-for="(modeEnum, index) in modes" :key="index" :value="modeEnum.value">{{ modeEnum.label }}</option>
          </select>
          <div v-if="validationErrors.mode" class="invalid-feedback">
            {{ validationErrors.mode[0] }}
          </div>
        </div>

        <div class="form-group mb-3">
          <label for="frm-create-payment-status">{{ $t('Status:') }}</label>
          <select id="frm-create-payment-status" name="status" :class="['form-control', validationErrors.status ? 'is-invalid' : '']" v-model="status">
            <option v-for="(s, index) in statusList" :key="index" :value="s">{{ s }}</option>
          </select>
          <div v-if="validationErrors.status" class="invalid-feedback">
            {{ validationErrors.status[0] }}
          </div>
        </div>

        <div class="form-group mb-3">
          <label for="frm-create-payment-amount">{{ $t('Amount:') }}</label>
          {{ props.currency }}
          <InputPrice
            id="frm-create-payment-amount"
            input-name="paid_amount"
            :currency="props.currency"
            v-model="paidAmount"
            class="mb-3"
          >
          </InputPrice>
          <div v-if="validationErrors.paid_amount" class="invalid-feedback">
            {{ validationErrors.paid_amount[0] }}
          </div>
        </div>

        <div class="form-group mb-3">
          <label for="frm-create-payment-reference">{{ $t('Reference (optional):') }}</label>
          <input id="frm-create-payment-reference" type="text" name="reference" v-model="reference"
                 :class="['form-control', validationErrors.reference ? 'is-valid' : '']">
          <div v-if="validationErrors.reference" class="invalid-feedback">
            {{ validationErrors.reference[0] }}
          </div>
        </div>
      </div>
      <DialogFooter>
        <button type="submit" class="btn btn-primary"
                :disabled="disableButton"
                @click.prevent="onSubmitPayment()">
          {{ $t('Save payment') }}
        </button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>
